import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { currencySymbol } from '../constants/currency';
import api, { type SchoolMember, apiErrorMessage, type Course, type Student, type Payment } from '../services/api';
import CourseAttendanceView from '../components/CourseAttendanceView';

const COLORS = ['#6C5CE7', '#00B894', '#FD79A8', '#FDCB6E', '#0984E3', '#E17055', '#00CEC9', '#636E72'];
const DAYS_OPTIONS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function getTimeDisplay(item: { start_time?: string; end_time?: string; time?: string }): string {
  const start = item.start_time || item.time || '';
  const end = item.end_time || '';
  if (start && end) {
    return `${start} — ${end}`;
  }
  return start || '—';
}

interface GroupItem {
  id: string;
  course_id: string;
  name: string;
  days: string;
  start_time: string;
  end_time: string;
  location: string;
  location_link: string;
  teacher: string;
  /** Внутренний user_id учителя команды, которому назначен урок (пусто — не назначен). */
  teacher_user_id?: string;
  student_ids: string;
  student_count: number;
  is_active: string;
  students?: any[];
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatDays(days: string): string {
  if (!days) return '';
  return days.split(',').join(', ');
}

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  const isAdmin = permissions.canManageUsers;
  const curSymbol = currencySymbol(user?.currency);
  // ЭТАП 8: в команде урок назначается учителю школы (вне школы — свободный текст).
  const isSchoolTeam = permissions.canManageSchoolTeam;

  const [course, setCourse] = useState<Course | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [groups, setGroups] = useState<GroupItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'students' | 'groups' | 'payments' | 'attendance'>('students');

  // ── Create Group modal state ─────────────────────────────────────────
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState<GroupItem | null>(null);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [savingEditGroup, setSavingEditGroup] = useState(false);
  // Участники команды школы, которым можно назначить урок
  const [teamMembers, setTeamMembers] = useState<SchoolMember[]>([]);
  const [groupForm, setGroupForm] = useState({
    name: '',
    days: [] as string[],
    start_time: '',
    end_time: '',
    location: '',
    location_link: '',
    teacher: '',
    teacher_user_id: '',
  });

  // Состав команды нужен только тем, кто ведёт школу (владелец/админ).
  useEffect(() => {
    if (!isSchoolTeam) return;
    let cancelled = false;
    api.getMySchool()
      .then(d => {
        if (cancelled) return;
        setTeamMembers(
          (d.members || []).filter(m => m.role === 'teacher' || m.role === 'admin'),
        );
      })
      .catch(() => { if (!cancelled) setTeamMembers([]); });
    return () => { cancelled = true; };
  }, [isSchoolTeam]);

  const memberName = (userId: string): string => {
    const m = teamMembers.find(x => x.id === userId);
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') : '';
  };

  // ── Toast state ───────────────────────────────────────────────────────
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  // ── Edit course state ────────────────────────────────────────────────
  const [showEditCourse, setShowEditCourse] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    price: '',
    monthly_price: '',
    lesson_price: '',
    lessons_per_week: '',
    payment_type: '',
    color: '#6C5CE7',
    location: '',
    location_link: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditCourse = () => {
    if (!course) return;
    setEditForm({
      title: course.title,
      description: course.description || '',
      price: course.price || '',
      monthly_price: course.monthly_price || '',
      lesson_price: course.lesson_price || '',
      lessons_per_week: course.lessons_per_week || '',
      payment_type: course.payment_type || 'monthly',
      color: course.color || '#6C5CE7',
      location: course.location || '',
      location_link: course.location_link || '',
    });
    setShowEditCourse(true);
  };

  const handleEditCourseSave = async () => {
    if (!course || !editForm.title.trim()) return;
    setSavingEdit(true);
    try {
      await api.updateCourse(course.id, {
        title: editForm.title,
        description: editForm.description || undefined,
        price: editForm.price,
        monthly_price: editForm.monthly_price || undefined,
        lesson_price: editForm.lesson_price || undefined,
        lessons_per_week: editForm.lessons_per_week || undefined,
        payment_type: editForm.payment_type || undefined,
        color: editForm.color,
      });
      setShowEditCourse(false);
      // Reload course data
      loadCourse(course.id);
    } catch (e) {
      console.error('Failed to update course:', e);
      alert(apiErrorMessage(e, 'Ошибка при сохранении курса'));
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (!id) {
      setError('Не указан ID курса');
      setLoading(false);
      return;
    }
    loadCourse(id);
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [id]);

  const loadCourse = async (courseId: string) => {
    try {
      setLoading(true);
      setError(null);

      const [courseData, groupsData, paymentsData] = await Promise.all([
        api.getCourse(courseId),
        api.getCourseGroups(courseId).catch(() => ({ groups: [] })),
        api.getPayments().catch(() => ({ payments: [] })),
      ]);

      setCourse(courseData);
      setGroups(groupsData.groups || []);
      setPayments((paymentsData.payments || []).filter((p: Payment) => p.course_id === courseId));

      // ── Collect unique student IDs from all groups of this course ─────
      const uniqueStudentIds = new Set<string>();
      for (const group of (groupsData.groups || [])) {
        const ids = group.student_ids
          ? group.student_ids.split(',').map((s: string) => s.trim()).filter(Boolean)
          : [];
        for (const id of ids) {
          uniqueStudentIds.add(id);
        }
      }

      // Fetch student details for all unique IDs
      if (uniqueStudentIds.size > 0) {
        const studentPromises = Array.from(uniqueStudentIds).map(id =>
          api.getStudent(id).catch(() => null as any)
        );
        const fetchedStudents = await Promise.all(studentPromises);
        setStudents(fetchedStudents.filter(Boolean));
      } else {
        setStudents([]);
      }
    } catch (e) {
      console.error('CourseDetailPage: Failed to load course:', e);
      setError(e instanceof Error ? e.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  // ── Stats ───────────────────────────────────────────────────────────

  const studentCount = students.length;
  const activeStudents = students.filter(s => s.is_active !== 'false').length;
  const groupCount = groups.length;
  const totalPaid = useMemo(() => {
    return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [payments]);

  const openCreateGroup = () => {
    // Auto-generate name: "{Course title} {next letter}"
    const nextLetter = String.fromCharCode(65 + groups.length); // A=65, B=66, ...
    const autoName = course ? `${course.title} ${nextLetter}` : '';
    setGroupForm({
      name: autoName,
      days: [],
      start_time: '',
      end_time: '',
      location: '',
      location_link: '',
      teacher: '',
      teacher_user_id: '',
    });
    setShowGroupForm(true);
  };

  const toggleGroupDay = (day: string) => {
    setGroupForm(f => ({
      ...f,
      days: f.days.includes(day) ? f.days.filter(d => d !== day) : [...f.days, day],
    }));
  };

  const toggleEditGroupDay = (day: string) => {
    setEditGroupForm(f => ({
      ...f,
      days: f.days.includes(day) ? f.days.filter(d => d !== day) : [...f.days, day],
    }));
  };

  const handleCreateGroup = async () => {
    if (!course || !groupForm.name.trim() || !groupForm.start_time) return;
    setSavingGroup(true);
    try {
      await api.createGroup({
        course_id: course.id,
        name: groupForm.name,
        days: groupForm.days,
        start_time: groupForm.start_time,
        location: groupForm.location || undefined,
        location_link: groupForm.location_link || undefined,
        teacher: isSchoolTeam
          ? (memberName(groupForm.teacher_user_id) || undefined)
          : (groupForm.teacher || undefined),
        // teacher_user_id передаём только в школе: у одиночки его нет вовсе
        // (а пустое значение в школе — это осознанное «снять преподавателя»).
        ...(isSchoolTeam ? { teacher_user_id: groupForm.teacher_user_id } : {}),
      });
      setShowGroupForm(false);
      await loadCourse(course.id);
      showToast(`Группа «${groupForm.name}» создана`);
    } catch (e) {
      console.error('Failed to create group:', e);
      showToast('Ошибка при создании группы');
    } finally {
      setSavingGroup(false);
    }
  };

  // ── Edit Group ────────────────────────────────────────────────────
  const [editGroupForm, setEditGroupForm] = useState({
    name: '',
    days: [] as string[],
    start_time: '',
    end_time: '',
    location: '',
    location_link: '',
    teacher: '',
    teacher_user_id: '',
  });

  const openEditGroupModal = (group: GroupItem) => {
    setEditingGroup(group);
    setEditGroupForm({
      name: group.name,
      days: group.days ? group.days.split(',').filter(Boolean) : [],
      start_time: group.start_time,
      end_time: group.end_time,
      location: group.location || '',
      location_link: group.location_link || '',
      teacher: group.teacher || '',
      teacher_user_id: group.teacher_user_id || '',
    });
    setShowEditGroupModal(true);
  };

  const handleEditGroupSave = async () => {
    if (!editingGroup || !editGroupForm.name.trim() || !editGroupForm.start_time) return;
    setSavingEditGroup(true);
    try {
      await api.updateGroup(editingGroup.id, {
        name: editGroupForm.name,
        days: editGroupForm.days,
        start_time: editGroupForm.start_time,
        end_time: editGroupForm.end_time || undefined,
        location: editGroupForm.location || undefined,
        location_link: editGroupForm.location_link || undefined,
        teacher: isSchoolTeam
          ? (memberName(editGroupForm.teacher_user_id) || undefined)
          : (editGroupForm.teacher || undefined),
        // Пустая строка очищает назначение (см. group_service._sanitize_assignment).
        ...(isSchoolTeam ? { teacher_user_id: editGroupForm.teacher_user_id } : {}),
      });
      setShowEditGroupModal(false);
      setEditingGroup(null);
      if (course) await loadCourse(course.id);
    } catch (e) {
      console.error('Failed to update group:', e);
      alert(apiErrorMessage(e, 'Ошибка при сохранении группы'));
    } finally {
      setSavingEditGroup(false);
    }
  };

  // ── Loading state ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="p-4 space-y-4 animate-fade-in">
        <div className="skeleton h-6 w-20 mb-2" />
        <div className="skeleton h-40 w-full rounded-3xl" />
        <div className="skeleton h-20 w-full rounded-2xl" />
        <div className="skeleton h-48 w-full rounded-2xl" />
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────

  if (error || !course) {
    return (
      <div className="p-4 animate-fade-in min-h-full flex flex-col items-center justify-center">
        <div className="tg-card flex flex-col items-center py-12 px-6 text-center max-w-sm mx-auto">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-5 text-[var(--tg-theme-hint-color)]"><circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
          <h2 className="text-lg font-semibold text-[var(--tg-theme-text-color)] mb-2">
            Курс не найден
          </h2>
          <p className="text-sm text-[var(--tg-theme-hint-color)] leading-relaxed">
            {error || 'Такого курса не существует или у вас нет доступа'}
          </p>
          <button
            onClick={() => navigate('/school/courses')}
            className="mt-6 tg-button text-sm flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Назад к курсам
          </button>
        </div>
      </div>
    );
  }

  const courseColor = course.color || '#6C5CE7';

  return (
    <div className="p-4 space-y-4 animate-fade-in pb-24">
      {/* ── Back button ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/school/courses')}
          className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1 hover:opacity-80 transition-opacity"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Назад
        </button>
        {permissions.canManageCourses && (
          <button onClick={openEditCourse}
            className="text-xs px-2.5 py-1 rounded-full font-medium border flex items-center gap-1 hover:bg-[var(--tg-theme-button-color)]/10 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Редактировать курс
          </button>
        )}
      </div>

      {/* ── Course Hero ──────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg"
        style={{ backgroundColor: courseColor }}
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/5" />

        <div className="relative">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold truncate">{course.title}</h1>
              {course.description && (
                <p className="text-sm text-white/80 mt-1 line-clamp-2">{course.description}</p>
              )}
            </div>
            <div className="text-right shrink-0 ml-3">
              <span className="text-3xl font-bold">{studentCount}</span>
              <p className="text-[10px] text-white/70">учеников</p>
            </div>
          </div>

          {/* Details row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4">
            {course.duration && (
              <div className="flex items-center gap-1.5 text-xs text-white/80">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span>{course.duration} мин</span>
              </div>
            )}
            {course.lessons_per_week && (
              <div className="flex items-center gap-1.5 text-xs text-white/80">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span>{course.lessons_per_week} з./нед</span>
              </div>
            )}
          </div>

          {/* Price */}
          {course.price && (
            <div className="mt-3 pt-3 border-t border-white/20">
              <span className="text-lg font-bold">
                {parseFloat(course.price).toLocaleString()} {curSymbol}
              </span>
              {course.monthly_price && (
                <span className="text-xs text-white/70 ml-2">
                  / {parseFloat(course.monthly_price).toLocaleString()} {curSymbol} абонемент
                </span>
              )}
              {course.lesson_price && (
                <span className="text-xs text-white/60 ml-2">
                  разово: {parseFloat(course.lesson_price).toLocaleString()} {curSymbol}
                </span>
              )}
            </div>
          )}

          {/* Location */}
          {course.location && (
            <div className="mt-2 pt-2 border-t border-white/20">
              <div className="flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle text-white/80"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {course.location_link ? (
                  <a
                    href={course.location_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-xs underline underline-offset-2 hover:opacity-80 transition-opacity"
                  >
                    {course.location}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="inline ml-0.5 -mt-0.5">
                      <path d="M7 17L17 7" /><path d="M7 7h10v10" />
                    </svg>
                  </a>
                ) : (
                  <span className="text-xs text-white/80">{course.location}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Stats cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-2">
        <div className="tg-card text-center py-3">
          <span className="text-xl font-bold text-[var(--tg-theme-text-color)]">{activeStudents}</span>
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Активных</p>
        </div>
        <div className="tg-card text-center py-3">
          <span className="text-xl font-bold text-[var(--tg-theme-text-color)]">{groupCount}</span>
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Групп</p>
        </div>
        <div className="tg-card text-center py-3">
          <span className="text-xl font-bold text-green-600">{totalPaid.toLocaleString()} {curSymbol}</span>
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">Оплат</p>
        </div>
      </div>

      {/* ── Quick actions ────────────────────────────────────────────── */}
      <div className="flex gap-2">
        {permissions.canManageGroups && (
          <button
            onClick={openCreateGroup}
            className="tg-button flex-1 text-sm py-3 flex items-center justify-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Новая группа
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => navigate('/school/students')}
            className="tg-button-secondary flex-1 text-sm py-3 flex items-center justify-center gap-2"
          >
            <img src="/icons/student-girl_pretty.svg" alt="" width="16" height="16" className="inline align-middle object-contain" /> Ученики
          </button>
        )}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────── */}
      <div className="flex gap-1 p-0.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)]">
        {[
          { key: 'students' as const, label: 'Ученики', icon: '/icons/student-girl_pretty.svg', count: studentCount },
          { key: 'groups' as const, label: 'Группы', icon: '/icons/group.svg', count: groupCount },
          { key: 'payments' as const, label: 'Оплаты', icon: '/icons/payments.svg', count: payments.length },
          { key: 'attendance' as const, label: 'Посещаемость', icon: '/icons/занятия(school-page).svg', count: 0 },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab.key
                ? 'bg-[var(--tg-theme-bg-color)] text-[var(--tg-theme-text-color)] shadow-sm'
                : 'text-[var(--tg-theme-hint-color)] hover:opacity-70'
            }`}
          >
            <img src={tab.icon} alt="" width="14" height="14" className="inline align-middle object-contain" /> {tab.label} {tab.count > 0 && `(${tab.count})`}
          </button>
        ))}
      </div>

      {/* ── Tab Content: Students ────────────────────────────────────── */}
      {activeTab === 'students' && (
        <div className="space-y-1.5">
          {students.length === 0 ? (
            <div className="tg-card flex flex-col items-center py-8 text-center">
              <img src="/icons/student-girl_pretty.svg" alt="" width="48" height="48" className="mb-3 object-contain" />
              <p className="text-sm text-[var(--tg-theme-hint-color)]">Учеников пока нет. Добавьте учеников в группы</p>
            </div>
          ) : (
            students.map(student => {
              const isActive = student.is_active !== 'false';
              return (
                <button
                  key={student.id}
                  onClick={() => navigate(`/student/${student.id}`)}
                  className="w-full text-left tg-card group hover:shadow-md transition-all duration-200 active:scale-[0.98]"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-sm font-bold shrink-0 ${
                      isActive
                        ? 'bg-gradient-to-br from-[var(--tg-theme-button-color)] to-[var(--tg-theme-button-color)]/70 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}>
                      {student.first_name?.[0] || '?'}{student.last_name?.[0] || ''}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-semibold truncate block ${isActive ? 'text-[var(--tg-theme-text-color)]' : 'text-gray-400'}`}>
                        {student.first_name} {student.last_name}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {student.age && (
                          <span className="text-[10px] text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> {student.age}</span>
                        )}
                        {student.phone && (
                          <span className="text-[10px] text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg> {student.phone}</span>
                        )}
                      </div>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      className="text-[var(--tg-theme-hint-color)] shrink-0 group-hover:translate-x-0.5 transition-transform">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}

      {/* ── Tab Content: Groups ───────────────────────────────────────── */}
      {activeTab === 'groups' && (
        <div className="space-y-2">
          {groups.length === 0 && !showGroupForm ? (
            <div className="tg-card flex flex-col items-center py-8 text-center">
              <img src="/icons/group.svg" alt="" width="48" height="48" className="mb-3 object-contain" />
              <p className="text-sm text-[var(--tg-theme-hint-color)] mb-3">Пока нет групп по этому курсу</p>
              {permissions.canManageGroups && (
                <button onClick={openCreateGroup} className="tg-button text-sm py-2 px-4">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Создать группу
                </button>
              )}
            </div>
          ) : (
            groups.map(group => {
              const studentIds = group.student_ids ? group.student_ids.split(',').filter(Boolean) : [];
              return (
                <div key={group.id} className="tg-card hover:shadow-md transition-all duration-200">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => navigate(`/school/lessons?course_id=${group.course_id}&group_id=${group.id}`)}
                      className="flex items-start gap-3 flex-1 min-w-0 text-left active:scale-[0.98] transition-transform"
                    >
                      <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[var(--tg-theme-button-color)] to-[var(--tg-theme-button-color)]/70 flex items-center justify-center shrink-0">
                        <img src="/icons/group.svg" alt="" width="20" height="20" className="object-contain" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-[var(--tg-theme-text-color)] truncate block">
                          {group.name}
                        </span>
                        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1">
                          {group.days && (
                            <span className="text-[11px] text-[var(--tg-theme-hint-color)]">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> {formatDays(group.days)}
                            </span>
                          )}
                          {group.start_time && (
                            <span className="text-[11px] text-[var(--tg-theme-hint-color)]">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> {group.start_time}{group.end_time ? `—${group.end_time}` : ''}
                            </span>
                          )}
                          <span className="text-[11px] text-[var(--tg-theme-hint-color)]">
                            <img src="/icons/student-girl_pretty.svg" alt="" width="10" height="10" className="inline align-middle object-contain" /> {group.student_count || studentIds.length || 0}
                          </span>
                        </div>
                        {group.teacher && (
                          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5">
                            <img src="/icons/role_teacher.svg" alt="" width="10" height="10" className="inline align-middle object-contain" /> {group.teacher}
                          </p>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditGroupModal(group); }}
                      className="shrink-0 mt-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium border border-[var(--tg-theme-section-separator-color)] hover:bg-[var(--tg-theme-secondary-bg-color)] transition-all active:scale-90"
                      title="Редактировать группу"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── Tab Content: Attendance ──────────────────────────────────── */}
      {activeTab === 'attendance' && (
        <CourseAttendanceView courseId={course.id} />
      )}

      {/* ── Tab Content: Payments ─────────────────────────────────────── */}
      {activeTab === 'payments' && (
        <div className="space-y-1.5">
          {payments.length === 0 ? (
            <div className="tg-card flex flex-col items-center py-8 text-center">
              <img src="/icons/payments.svg" alt="" width="48" height="48" className="mb-3 object-contain" />
              <p className="text-sm text-[var(--tg-theme-hint-color)]">Пока нет платежей по этому курсу</p>
            </div>
          ) : (
            <>
              <div className="tg-card !py-3 !px-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[var(--tg-theme-text-color)]">Всего получено</span>
                  <span className="text-lg font-bold text-green-600">{totalPaid.toLocaleString()} {curSymbol}</span>
                </div>
              </div>
              {payments
                .sort((a, b) => b.payment_date.localeCompare(a.payment_date))
                .map(payment => {
                  const student = students.find(s => s.id === payment.student_id);
                  return (
                    <div key={payment.id} className="tg-card !py-3 !px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-[var(--tg-theme-text-color)] block truncate">
                            {student ? `${student.first_name} ${student.last_name}` : 'Ученик удалён'}
                          </span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-[var(--tg-theme-hint-color)]">
                              {formatDate(payment.payment_date)}
                            </span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                              payment.payment_type === 'monthly' ? 'bg-blue-50 text-blue-600' :
                              payment.payment_type === 'partial' ? 'bg-amber-50 text-amber-600' :
                              payment.payment_type === 'full' ? 'bg-green-50 text-green-600' :
                              'bg-gray-50 text-gray-600'
                            }`}>
                              {payment.payment_type === 'monthly' ? 'Абонемент' :
                               payment.payment_type === 'partial' ? 'Частично' :
                               payment.payment_type === 'full' ? 'Полностью' :
                               payment.payment_type === 'per_lesson' ? 'Разовое' :
                               payment.payment_type || '—'}
                            </span>
                          </div>
                          {payment.comment && (
                            <p className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5 truncate">{payment.comment}</p>
                          )}
                        </div>
                        <span className="text-sm font-bold text-green-600 shrink-0 ml-3">
                          +{parseFloat(payment.amount).toLocaleString()} {curSymbol}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </>
          )}
        </div>
      )}
      {/* ── Create Group Modal (overlay) ──────────────────────────────── */}
      {showGroupForm && course && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowGroupForm(false)}>
          <div className="w-full max-w-lg bg-[var(--tg-theme-bg-color)] rounded-3xl p-6 shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Новая группа</h3>
              <button onClick={() => setShowGroupForm(false)} className="p-1 text-[var(--tg-theme-hint-color)]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">Название группы *</label>
                <input value={groupForm.name} onChange={e => setGroupForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Робототехника Junior A"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              {/* Start time + auto end_time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Начало *</label>
                  <input type="time" value={groupForm.start_time} onChange={e => setGroupForm(f => ({ ...f, start_time: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Конец (авто)</label>
                  <div className="px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)]/50 text-sm text-[var(--tg-theme-hint-color)]">
                    {course?.duration && groupForm.start_time ? (
                      (() => {
                        const [h, m] = groupForm.start_time.split(':').map(Number);
                        const total = h * 60 + m + parseInt(course.duration || '90');
                        return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
                      })()
                    ) : (
                      'Рассчитывается автоматически'
                    )}
                  </div>
                </div>
              </div>

              {/* Days selector */}
              <div>
                <p className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> Дни недели</p>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OPTIONS.map(day => (
                    <button key={day} onClick={() => toggleGroupDay(day)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        groupForm.days.includes(day)
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                          : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      }`}>
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> Место / Адрес</label>
                <input value={groupForm.location} onChange={e => setGroupForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="ул. Московская, д. 10, каб. 301"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              {/* Teacher */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><img src="/icons/role_teacher.svg" alt="" width="10" height="10" className="inline align-middle object-contain" /> Преподаватель</label>
                {isSchoolTeam ? (
                  <select value={groupForm.teacher_user_id}
                    onChange={e => setGroupForm(f => ({ ...f, teacher_user_id: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2">
                    <option value="">Не назначен</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>
                        {[m.first_name, m.last_name].filter(Boolean).join(' ')}{m.role === 'admin' ? ' (админ)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input value={groupForm.teacher} onChange={e => setGroupForm(f => ({ ...f, teacher: e.target.value }))}
                    placeholder="Иванова М. С."
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                )}
                {isSchoolTeam && (
                  <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
                    Урок появится в расписании выбранного учителя — только его он и будет вести.
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowGroupForm(false)} className="tg-button-secondary flex-1 text-sm">Отмена</button>
                <button onClick={handleCreateGroup}
                  disabled={savingGroup || !groupForm.name.trim() || !groupForm.start_time}
                  className="tg-button flex-1 text-sm disabled:opacity-50">
                  {savingGroup ? 'Создание...' : 'Создать группу'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Group Modal ─────────────────────────────────────────── */}
      {showEditGroupModal && editingGroup && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowEditGroupModal(false)}>
          <div className="w-full max-w-lg bg-[var(--tg-theme-bg-color)] rounded-3xl p-6 shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Редактировать группу</h3>
              <button onClick={() => setShowEditGroupModal(false)} className="p-1 text-[var(--tg-theme-hint-color)]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">Название группы *</label>
                <input value={editGroupForm.name} onChange={e => setEditGroupForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Робототехника Junior A"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Начало *</label>
                  <input type="time" value={editGroupForm.start_time} onChange={e => setEditGroupForm(f => ({ ...f, start_time: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Конец</label>
                  <input type="time" value={editGroupForm.end_time} onChange={e => setEditGroupForm(f => ({ ...f, end_time: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> Дни недели</p>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OPTIONS.map(day => (
                    <button key={day} onClick={() => toggleEditGroupDay(day)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        editGroupForm.days.includes(day)
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                          : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      }`}>
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> Место / Адрес</label>
                <input value={editGroupForm.location} onChange={e => setEditGroupForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="ул. Московская, д. 10, каб. 301"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg> Ссылка на карты</label>
                <input value={editGroupForm.location_link} onChange={e => setEditGroupForm(f => ({ ...f, location_link: e.target.value }))}
                  placeholder="https://yandex.ru/maps/..."
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]"><img src="/icons/role_teacher.svg" alt="" width="10" height="10" className="inline align-middle object-contain" /> Преподаватель</label>
                {isSchoolTeam ? (
                  <select value={editGroupForm.teacher_user_id}
                    onChange={e => setEditGroupForm(f => ({ ...f, teacher_user_id: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2">
                    <option value="">Не назначен</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>
                        {[m.first_name, m.last_name].filter(Boolean).join(' ')}{m.role === 'admin' ? ' (админ)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input value={editGroupForm.teacher} onChange={e => setEditGroupForm(f => ({ ...f, teacher: e.target.value }))}
                    placeholder="Иванова М. С."
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowEditGroupModal(false)} className="tg-button-secondary flex-1 text-sm">Отмена</button>
                <button onClick={handleEditGroupSave} disabled={savingEditGroup || !editGroupForm.name.trim() || !editGroupForm.start_time}
                  className="tg-button flex-1 text-sm disabled:opacity-50">
                  {savingEditGroup ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast notification ──────────────────────────────────────────── */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] animate-slide-up">
          <div className="px-5 py-3 rounded-2xl bg-[var(--tg-theme-bg-color)] shadow-2xl border border-[var(--tg-theme-section-separator-color)] text-sm font-medium text-[var(--tg-theme-text-color)]">
            {toast}
          </div>
        </div>
      )}

      {/* ── Edit Course Modal ────────────────────────────────────────── */}
      {showEditCourse && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowEditCourse(false)}>
          <div className="w-full max-w-lg bg-[var(--tg-theme-bg-color)] rounded-3xl p-6 shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline align-middle"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Редактировать курс</h3>
              <button onClick={() => setShowEditCourse(false)} className="p-1 text-[var(--tg-theme-hint-color)]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">Название курса *</label>
                <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Робототехника Junior"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">Описание</label>
                <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Основы робототехники для начинающих"
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
              </div>

              {/* Price row */}
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">💰 Цена ({curSymbol})</label>
                  <input value={editForm.price} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                    placeholder="40000" type="number"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
              </div>

              {/* Tariff fields */}
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">📅 Абонемент ({curSymbol})</label>
                  <input value={editForm.monthly_price} onChange={e => setEditForm(f => ({ ...f, monthly_price: e.target.value }))}
                    placeholder="40000" type="number"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">🎫 Разовое ({curSymbol})</label>
                  <input value={editForm.lesson_price} onChange={e => setEditForm(f => ({ ...f, lesson_price: e.target.value }))}
                    placeholder="5000" type="number"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-medium text-[var(--tg-theme-hint-color)]">📋 Занятий/нед</label>
                  <input value={editForm.lessons_per_week} onChange={e => setEditForm(f => ({ ...f, lessons_per_week: e.target.value }))}
                    placeholder="2" type="number"
                    className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2" />
                </div>
              </div>

              {/* Color picker */}
              <div>
                <p className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2">🎨 Цвет</p>
                <div className="flex gap-2">
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setEditForm(f => ({ ...f, color: c }))}
                      className={`w-8 h-8 rounded-full transition-all ${editForm.color === c ? 'ring-2 ring-offset-2 ring-[var(--tg-theme-button-color)] scale-110' : ''}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowEditCourse(false)} className="tg-button-secondary flex-1 text-sm">Отмена</button>
                <button onClick={handleEditCourseSave} disabled={savingEdit || !editForm.title.trim()}
                  className="tg-button flex-1 text-sm disabled:opacity-50">
                  {savingEdit ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
